/**
 * The six FX functions' response processors (LSEG's SpotResProcessor,
 * SwapToSwapResProcessor, SwapToDepResProcessor, DepToSwapResProcessor,
 * ZcToFraResProcessor and InfoResProcessor), ported from Workspace's Excel
 * add-in (Refinitiv for Office 26.30.2). Each names its columns and reads
 * LSEG's answer into rows; BaseAdFinResProcessor lays them out.
 *
 * The swap functions and FxInfo lay out one row per tenor or currency code of
 * the request, in the request's order (`getDataRowsInOrder`).
 */

import { AdFinInputContract } from "./baseProcessor";
import { FxFn } from "./processors";
import { BaseAdFinResProcessor, ClientResponse, ResponseHeader, RowLabels } from "./responseBase";

/**
 * The label rows of a swap function or FxInfo: the request's `labelsKey`
 * list, keyed by `rowLabel`. The user's Fields (the Excel argument at
 * `fieldsArg`, LSEG's fixed position for each function) keep the label's
 * column when they are blank or name it.
 */
// LSEG: the `d` / `p` / `l` object of SwapToSwapResProcessor_15334, SwapToDepResProcessor_74574,
// DepToSwapResProcessor_48742 and InfoResProcessor_71688.getDataFromResponse
function rowLabels(
  contract: AdFinInputContract,
  request: Record<string, unknown> | undefined,
  labelsKey: string,
  rowLabel: string,
  fieldsArg: number,
): RowLabels {
  const fields = contract.args[contract.argIndexMap?.fields ?? -1]?.toString();
  const labels = request && request[labelsKey] ? (request[labelsKey] as unknown[]) : [];
  return { labels, rowLabel, keyFieldExist: !contract.args[fieldsArg] || fields?.toLowerCase().includes(rowLabel.toLowerCase()) };
}

// LSEG: SpotResProcessor_17980.SpotResProcessor
export class FxSpotResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [
      { name: "spotDate", type: "Date" },
      { name: "spotCcy1Bid", type: "Float" },
      { name: "spotCcy1Ask", type: "Float" },
      { name: "spotCcy2Bid", type: "Float" },
      { name: "spotCcy2Ask", type: "Float" },
      { name: "spotCcy1Ccy2Bid", type: "Float" },
      { name: "spotCcy1Ccy2Ask", type: "Float" },
      ...super.getHeaders(),
    ];
  }

  /** One row: `crossRates` (an object, or the first of a list). */
  // LSEG: SpotResProcessor_17980.getDataFromResponse
  getDataFromResponse(response: unknown, contract: AdFinInputContract): ClientResponse {
    const key = "crossRates";
    if (!this.checkValidResponse(response, key)) return this.getBadResponse("Spot", key, response);
    const rates = this.getValidatedProperty(response, key);
    const row = Array.isArray(rates) ? rates[0] : (rates ?? {});
    return this.getDataRows({ headers: this.getHeaders(), data: [this.buildRowData(row)] }, contract);
  }
}

// LSEG: SwapToSwapResProcessor_15334.SwapToSwapResProcessor
export class FxSwpToSwpResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [
      { name: "tenor", type: "String" },
      { name: "startDate", type: "Date" },
      { name: "endDate", type: "Date" },
      { name: "swapCcy1Bid", type: "Float" },
      { name: "swapCcy1Ask", type: "Float" },
      { name: "swapCcy2Bid", type: "Float" },
      { name: "swapCcy2Ask", type: "Float" },
      { name: "swapCcy1Ccy2Bid", type: "Float" },
      { name: "swapCcy1Ccy2Ask", type: "Float" },
      { name: "outrightCcy1Ccy2Bid", type: "Float" },
      { name: "outrightCcy1Ccy2Ask", type: "Float" },
      ...super.getHeaders(),
    ];
  }

  /** One row per requested tenor, from `swapPoints`. */
  // LSEG: SwapToSwapResProcessor_15334.getDataFromResponse
  getDataFromResponse(response: unknown, contract: AdFinInputContract, request?: Record<string, unknown>): ClientResponse {
    const key = "swapPoints";
    if (!this.checkValidResponse(response, key)) return this.getBadResponse("SwapToSwap", key, response);
    const processingInformation = this.getValidatedProperty(response, "processingInformation");
    const rows = this.getValidatedDataRows(response, key, processingInformation) ?? [];
    const order = rowLabels(contract, request, "tenors", "tenor", 9);
    const data = this.getDataRowsInOrder(order, rows)!.map((row) => this.buildRowData(row));
    return this.getDataRows({ headers: this.getHeaders(), data }, contract, order);
  }
}

// LSEG: SwapToDepResProcessor_74574.SwapToDepResProcessor
export class FxSwpToDepResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [
      { name: "tenor", type: "String" },
      { name: "startDate", type: "Date" },
      { name: "endDate", type: "Date" },
      { name: "swapCcy1Bid", type: "Float" },
      { name: "swapCcy1Ask", type: "Float" },
      { name: "swapCcy2Bid", type: "Float" },
      { name: "swapCcy2Ask", type: "Float" },
      { name: "depCcy1Bid", type: "Float" },
      { name: "depCcy1Ask", type: "Float" },
      { name: "depCcy2Bid", type: "Float" },
      { name: "depCcy2Ask", type: "Float" },
      ...super.getHeaders(),
    ];
  }

  /** One row per requested tenor, from `depositRates`; an answer nested one level deeper (`data[0].data`) is read inside. */
  // LSEG: SwapToDepResProcessor_74574.getDataFromResponse
  getDataFromResponse(response: unknown, contract: AdFinInputContract, request?: Record<string, unknown>): ClientResponse {
    const nested = (response as { data?: { data?: unknown }[] } | null)?.data?.[0];
    if (nested?.data) return this.getDataFromResponse(nested, contract, request);
    const key = "depositRates";
    if (!this.checkValidResponse(response, key)) return this.getBadResponse("SwapToDep", key, response);
    const processingInformation = this.getValidatedProperty(response, "processingInformation");
    const rows = this.getValidatedDataRows(response, key, processingInformation) ?? [{}];
    const order = rowLabels(contract, request, "tenors", "tenor", 10);
    const data = this.getDataRowsInOrder(order, rows)!.map((row) => this.buildRowData(row));
    return this.getDataRows({ headers: this.getHeaders(), data }, contract, order);
  }
}

// LSEG: DepToSwapResProcessor_48742.DepToSwapResProcessor
export class FxDepToSwpResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [
      { name: "tenor", type: "String" },
      { name: "startDate", type: "Date" },
      { name: "endDate", type: "Date" },
      { name: "depCcy1Bid", type: "Float" },
      { name: "depCcy1Ask", type: "Float" },
      { name: "depCcy2Bid", type: "Float" },
      { name: "depCcy2Ask", type: "Float" },
      { name: "swapCcy1Ccy2Bid", type: "Float" },
      { name: "swapCcy1Ccy2Ask", type: "Float" },
      { name: "outrightCcy1Ccy2Bid", type: "Float" },
      { name: "outrightCcy1Ccy2Ask", type: "Float" },
      ...super.getHeaders(),
    ];
  }

  /** One row per requested tenor, from `swapPoints`. */
  // LSEG: DepToSwapResProcessor_48742.getDataFromResponse
  getDataFromResponse(response: unknown, contract: AdFinInputContract, request?: Record<string, unknown>): ClientResponse {
    const key = "swapPoints";
    if (!this.checkValidResponse(response, key)) return this.getBadResponse("DepToSwap", key, response);
    const processingInformation = this.getValidatedProperty(response, "processingInformation");
    const rows = this.getValidatedDataRows(response, key, processingInformation) ?? [];
    const order = rowLabels(contract, request, "tenors", "tenor", 9);
    const data = this.getDataRowsInOrder(order, rows)!.map((row) => this.buildRowData(row));
    return this.getDataRows({ headers: this.getHeaders(), data }, contract, order);
  }
}

// LSEG: ZcToFraResProcessor_77578.ZcToFraResProcessor
export class FxZcToFraResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [
      { name: "StartDate", type: "String" },
      { name: "EndDate", type: "String" },
      { name: "Fra", type: "Number" },
    ];
  }

  /** One row: the first of `fras` (or of `swapPoints`). */
  // LSEG: ZcToFraResProcessor_77578.getDataFromResponse
  getDataFromResponse(response: unknown, contract: AdFinInputContract): ClientResponse {
    const keys = "fras' or 'swapPoints";
    if (!response) return this.getBadResponse("ZcToFra", keys, response);
    const list = (this.getValidatedProperty(response, "fras") ?? this.getValidatedProperty(response, "swapPoints")) as
      | Record<number, { startDate?: unknown; endDate?: unknown; fra?: unknown }>
      | null
      | undefined;
    const fra = list?.[0];
    if (!fra) return this.getBadResponse("ZcToFra", keys, response);
    const data = [[fra.startDate ?? "", fra.endDate ?? "", fra.fra ?? ""]];
    return this.getDataRows({ headers: this.getHeaders(), data }, contract);
  }
}

// LSEG: InfoResProcessor_71688.InfoResProcessor
export class FxInfoResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [
      { name: "currencyCode", type: "String" },
      { name: "currencyName", type: "String" },
      { name: "fxSpotLag", type: "Float" },
      { name: "mmSpotLag", type: "Float" },
      { name: "calendar", type: "String" },
      { name: "quotationMode", type: "String" },
      { name: "mmYearBasis", type: "Float" },
      ...super.getHeaders(),
    ];
  }

  /**
   * One row per requested currency code, from `styles`. Like LSEG, a
   * missing `styles` makes the layout fail (the formula cell then shows "Updated at").
   */
  // LSEG: InfoResProcessor_71688.getDataFromResponse
  getDataFromResponse(response: unknown, contract: AdFinInputContract, request?: Record<string, unknown>): ClientResponse {
    if (!response) return this.badSyntax("Info");
    const processingInformation = this.getValidatedProperty(response, "processingInformation");
    const rows = this.getValidatedDataRows(response, "styles", processingInformation);
    const order = rowLabels(contract, request, "currencyCodes", "currencyCode", 3);
    const data = (this.getDataRowsInOrder(order, rows) as unknown[]).map((row) => this.buildRowData(row));
    return this.getDataRows({ headers: this.getHeaders(), data }, contract, order);
  }
}

/** Each FX function's response processor. */
export const FX_RESPONSE_PROCESSORS: Record<FxFn, new () => BaseAdFinResProcessor> = {
  FxSpot: FxSpotResProcessor,
  FxSwpToSwp: FxSwpToSwpResProcessor,
  FxSwpToDep: FxSwpToDepResProcessor,
  FxDepToSwp: FxDepToSwpResProcessor,
  FxZcToFra: FxZcToFraResProcessor,
  FxInfo: FxInfoResProcessor,
};
